import { CodeMapSet, CodeMapSetService, CodeMapType } from "@app/codemaps";
import { County } from "@app/geography";
import { ClientInsuranceModel } from "@app/insurance";
import { MemberSetModel } from "@app/members";
import { ProviderSet } from "@app/provider-sets";
import { RegionSet } from "@app/region-sets";
import LookupRegionSet from "@app/region-sets/editor/lookupRegionSet";
import { AccessCriteriaGranularityEnum, TemplateCollectionMetadata, TemplateSpecialtyGroup } from "@app/templates";
import difference from "lodash/difference";
import some from "lodash/some";
import sortBy from "lodash/sortBy";
import { Route, RouteComponentProps, Switch, generatePath, useRouteMatch } from "react-router-dom";
import CreateProject from "@app/projects/create/createProject";
import { SpecialtyDefinition, SpecialtyDefinitionService } from "@app/specialty-definitions";
import CreateProjectOverrideMemberSet from "@app/projects/createProjectOverrideMemberSet";
import { ProjectOverrideMemberSetType, RegionSetStatusType, TimeDistanceCalculatorType } from "@app/projects/enumerators";
import { MemberSetTypeEnum } from "@app/members/enumerations";
import Features from "@app/feature-flags/features";
import { NetworkTaxonomyData } from "@app/network-taxonomies/networkTaxonomy";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ResourceType } from "@app/resources";
import { createProjectThingsToResetIfZipProject as createProjectThingsToResetOnAnalysisTypeChange } from "@app/projects/helpers";
import { ResourceTagModel } from "@app/tags/import/types";
import { FC, useContext, useEffect, useRef, useState } from "react";
import ServiceAreaContextProvider from "@app/region-sets/editor/ServiceAreaEditorContext";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import MemberSetService from "@app/members/memberSetService";
import TemplateService from "@app/templates/templateService";
import AccuracyTemplateService from "@app/templates/accuracy/accuracyTemplateService";
import { AccuracySpecialtyDefinition, AccuracyTemplateCollectionMetadata } from "@app/templates/accuracy";
import { ClientService } from "@app/client/clientService";
import InsuranceService from "@app/insurance/insuranceService";
import CreateProjectStepMembers from "@app/projects/create/CreateProjectStepMembers";
import CreateProjectStepNetworkTaxonomy from "@app/projects/create/CreateProjectStepNetworkTaxonomy";
import CreateProjectStepOptions from "@app/projects/create/CreateProjectStepOptions";
import CreateProjectStepProviders from "@app/projects/create/CreateProjectStepProviders";
import CreateProjectStepReview from "@app/projects/create/CreateProjectStepReview";
import CreateProjectStepServiceArea from "@app/projects/create/CreateProjectStepServiceArea";
import CreateProjectStepSetup from "@app/projects/create/CreateProjectStepSetup";
import CreateProjectStepTemplate from "@app/projects/create/CreateProjectStepTemplate";
import { CountyStatus } from "@app/projects/create/countyStatus";
import { SpecialtyOverrides, ProviderSetAliasKeys } from "@app/projects/create/interfaces";
import { applyPrimaryMembershipBindingChange, applySecondaryMembershipBindingChange } from "@app/projects/create/membershipBindingHelpers";

type UpdateResourceKeys = keyof CreateProject;

const CreateProjectWizard: FC = () => {
  const user = useContext(UserContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const routeMatch = useRouteMatch();
  const templateService = useRef(new TemplateService()).current;
  const accuracyTemplateService = useRef(new AccuracyTemplateService()).current;
  const memberSetService = useRef(new MemberSetService()).current;
  const codeMapSetService = useRef(new CodeMapSetService()).current;
  const specialtyDefinitionService = useRef(new SpecialtyDefinitionService()).current;
  const insuranceService = useRef(new InsuranceService()).current;
  const clientService = useRef(new ClientService()).current;

  const populateOverride = () => {
    const primary: CreateProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Primary,
      secondarySpecialtyGroupCodes: [],
    };
    const secondary: CreateProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Secondary,
      secondarySpecialtyGroupCodes: [],
    };
    const risk: CreateProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: "Risk Analysis Membership",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Risk,
      secondarySpecialtyGroupCodes: [],
    };
    const createProjectOverrideMemberSets: CreateProjectOverrideMemberSet[] = [];
    createProjectOverrideMemberSets.push(primary);
    createProjectOverrideMemberSets.push(secondary);
    if (some(user.roles, ((s) => s === Roles.MEMBERSHIP_RISK_USER))) { createProjectOverrideMemberSets.push(risk); }
    return createProjectOverrideMemberSets;
  }

  const getInitialCreateProjectModel = (): CreateProject => {
    return {
      accessCodeMapSetDescription: null,
      accessCodeMapSetId: null,
      accuracyAndAdequacy: false,
      accuracySpecialtyDefinitionDesc: null,
      accuracySpecialtyDefinitionId: null,
      accuracySpecialtyToQdpCodeMapSetDescription: null,
      accuracySpecialtyToQdpCodeMapSetId: null,
      accuracyTemplateCollectionDescription: "",
      accuracyTemplateCollectionId: null,
      accuracyTemplateCorrelationId: "",
      adequacyTemplateCollectionDescription: "",
      adequacyTemplateCollectionId: null,
      adequacyTemplateCorrelationId: "",
      affiliationsDisplayOnNetworkAccordion: false,
      autoArchiveUnarchiveSpecialtyExceptions: false,
      calculateStandards: false,
      countyStatuses: [],
      createProjectOverrideMemberSets: populateOverride(),
      description: "",
      enableRegulatoryExceptionReports: false,
      excludeDeactivatedFacilitiesImpact: false,
      excludeDeactivatedFacilitiesNetwork: false,
      excludeDeactivatedProvidersImpact: false,
      excludeDeactivatedProvidersNetwork: false,
      excludedSpecialtyGroupCodes: [],
      excludeInvalidNppesNpiFacilitiesImpact: false,
      excludeInvalidNppesNpiFacilitiesNetwork: false,
      excludeInvalidNppesNpiProvidersImpact: false,
      excludeInvalidNppesNpiProvidersNetwork: false,
      excludeInVolumeActions: false,
      excludeMedicareOptOutProvidersImpact: false,
      excludeMedicareOptOutProvidersNetwork: false,
      excludeMissingNpiFacilitiesImpact: false,
      excludeMissingNpiFacilitiesNetwork: false,
      excludeMissingNpiProvidersImpact: false,
      excludeMissingNpiProvidersNetwork: false,
      excludeOIGExcludedProvidersImpact: false,
      excludeOIGExcludedProvidersNetwork: false,
      excludePrecludedProvidersImpact: false,
      excludePrecludedProvidersNetwork: false,
      excludeProjectInSnowflakeDataShare: false,
      hasZipSummaries: false,
      impactExclusions: ["None"],
      includeGapAging: false,
      includeInEnterpriseComplianceDashboard: false,
      inspectedInsurancesList: [],
      isPrivate: false,
      marketProviderDescription: "",
      marketProviderSetId: null,
      marketSpecialtyCodeMapSetId: null,
      memberPopDescription: "",
      memberPopDescription2: "",
      memberSetAlias: "",
      memberSetAlias2: "",
      memberSetId: null,
      memberSetId2: null,
      membershipRiskMemberSetAlias: "",
      membershipRiskMemberSetId: null,
      name: "",
      ncpInStateProvidersOnly: false,
      networkExclusions: ["None"],
      networkProviderDescription: "",
      networkProviderSetId: null,
      networkSpecialtyCodeMapSetId: null,
      prospectProviderDescription: "",
      prospectProviderSetId: null,
      regulatoryDomainId: null,
      rppoNonContractedProvidersIncludedInHsd: false,
      serviceAreaRegionSetId: null,
      showAccuracyGapAnalysis: false,
      showAdvancedComparison: false,
      showAffiliationsInsights: false,
      showAttestation: false,
      showAverageDistance: false,
      showAverageTime: false,
      showCareJourneyScores: false,
      showClosestProviderReport: false,
      showCMSCredit: false,
      showLocationConfidenceScores: false,
      showMaximumDistance: false,
      showMaximumTime: false,
      showMipsScore: false,
      showMotiveAppropriatenessScore: false,
      showNetworkIntegrityAttestationBasedAlerts: false,
      showNetworkIntegrityCriticalAlerts: false,
      showNetworkIntegrityDashboard: false,
      showNextClosestNetworkProviders: false,
      showNppesDates: false,
      showPatternOfCareProviders: false,
      showPciDashboard: false,
      showPciData: false,
      showPgrConfidenceTier: false,
      showTricareClaimsVolumeColumn: false,
      showVaClaimsVolumeColumn: false,
      strictTimeAndDistance: false,
      tags: [
        {
          tagGroupName: "",
          tagName: "",
        }
      ],
      telehealthSpecialtyGroupCodes: [],
      timeDistanceCalculatorType: TimeDistanceCalculatorType.Quest,
      timeDistanceCalculatorTypes: [],
      useMarketProvidersForImpact: true,
      useMarketProvidersForMarketAnalysis: false,
      useNetworkProvidersForMarketAnalysis: false,
      useProspectProvidersForMarketAnalysis: false,
      useSnowflakeProviderSetsForAnalysis: false,
    } as CreateProject;
  }

  const [accessCodeMapDefaultSetId, setAccessCodeMapDefaultSetId] = useState<number>(null);
  const [accuracyIsLoading, setAccuracyIsLoading] = useState(false);
  const [accuracySpecialtyDefinitions, setAccuracySpecialtyDefinitions] = useState<AccuracySpecialtyDefinition[]>([]);
  const [accuracyTemplateCollections, setAccuracyTemplateCollections] = useState<AccuracyTemplateCollectionMetadata[]>([]);
  const [allAccessCodeMapSets, setAllAccessCodeMapSets] = useState<CodeMapSet[]>([]);
  const [allMemberPopulations, setAllMemberPopulations] = useState<MemberSetModel[]>([]);
  const [alternateMemberships, setAlternateMemberships] = useState<MemberSetModel[]>([]);
  const [authorizedInsurances, setAuthorizedInsurances] = useState<ClientInsuranceModel[]>([]);
  const [authorizedInsurancesErred, setAuthorizedInsurancesErred] = useState(false);
  const [createProjectModel, setCreateProjectModel] = useState<CreateProject>(getInitialCreateProjectModel());
  const [disableMemberBindingTable, setDisableMemberBindingTable] = useState(true);
  const [expandSpecialtyMileageCodeMapSets, setExpandSpecialtyMileageCodeMapSets] = useState([]);
  
  const [hideClosestProviderReportOption, setHideClosestProviderReportOption] = useState(false);
  const [hideCMSApplicationCreditOption, setHideCMSApplicationCreditOption] = useState(true);
  const [hideEnterpriseComplianceDashboard, setHideEnterpriseComplianceDashboard] = useState(true);
  const [hideMotiveAppropriatenessScore, setHideMotiveAppropriatenessScore] = useState(true);
  const [hideShowInMarketAnalysis, setHideShowInMarketAnalysis] = useState(true);
  const [hideStandardsCreation] = useState(!(enabledFeatureFlags.has(Features.STANDARD_SETTING_CREATION) && user.roles.includes(Roles.STANDARDS_CREATOR_USER)));
  const [hideStrictTimeDistance] = useState(enabledFeatureFlags.has(Features.HIDE_STRICT_TIME_DISTANCE));
  const [isDataShareEligible, setIsDataShareEligible] = useState(false);
  const [isNetworkTaxonomyValid, setIsNetworkTaxonomyValid] = useState(true);
  const [isZipProjectTemplate, setIsZipProjectTemplate] = useState(false);
  const [lookupRegionSet, setLookupRegionSet] = useState<LookupRegionSet>(null);
  const [networkProviderSetNetworkTaxonomy] = useState<NetworkTaxonomyData>(null);
  const [originalTemplateCollections] = useState<TemplateCollectionMetadata[]>([]);
  const [progressList, setProgressList] = useState<string[]>(["Initial Setup", "Template", "Members", "Providers", "Network Taxonomy", "Service Area", "Options", "Confirm",]);
  const [projectAccessCodeMapSets, setProjectAccessCodeMapSets] = useState<CodeMapSet[]>([]);
  const [projectCollections, setProjectCollections] = useState<TemplateCollectionMetadata[]>([]);
  const [projectMemberPopulations, setProjectMemberPopulations] = useState<MemberSetModel[]>([]);
  const [projectTemplateCollections] = useState<TemplateCollectionMetadata[]>([]);
  const [selectedInsurances, setSelectedInsurances] = useState<ClientInsuranceModel[]>([]);
  const [selectedInsuranceUid, setSelectedInsuranceUid] = useState("");
  const [serviceAreaRegionSet, setServiceAreaRegionSet] = useState<RegionSet>(null);
  const [showNetworkSpecialtyCodeMap, setShowNetworkSpecialtyCodeMap] = useState(false);
  const [specialtyCodeMapSets, setSpecialtyCodeMapSets] = useState<CodeMapSet[]>([]);
  const [specialtyDefinitionsMissing, setSpecialtyDefinitionsMissing] = useState(false);
  const [specialtyGroups, setSpecialtyGroups] = useState<TemplateSpecialtyGroup[]>([]);
  const [specialtyOverrides, setSpecialtyOverrides] = useState<SpecialtyOverrides[]>([]);
  const [specialtyToQdpCodeMapSets, setSpecialtyToQdpCodeMapSets] = useState<CodeMapSet[]>([]);
  const [timeDistanceCalculatorTypes, setTimeDistanceCalculatorTypes] = useState<TimeDistanceCalculatorType[]>([]);
  const [updateCounties, setUpdateCounties] = useState<County[]>([]);
  const [useAccuracy, setUseAccuracy] = useState(false);
  const [useAdequacy, setUseAdequacy] = useState(true);
  const [usesPlanningRegionTemplate, setUsesPlanningRegionTemplate] = useState(false);

  useEffect(() => {
    clientService.getClient(user.clientId).then((newClient) => {
      setShowNetworkSpecialtyCodeMap(!!newClient?.networkBenchmarkingDataSource);
      setIsDataShareEligible(!!newClient?.isDataShareEligible);
    })
  }, []);

  useEffect(() => {
    setProgressList(["Initial Setup", "Template", "Members", "Providers", "Network Taxonomy", "Service Area", "Options", "Confirm",]);
  }, [enabledFeatureFlags]);

  const accuracyTemplateComplete = (!!createProjectModel.accuracyTemplateCollectionId &&
    !!createProjectModel.accuracyTemplateCorrelationId &&
    createProjectModel.accuracyTemplateCorrelationId !== "");
  const adequacyTemplateComplete = (!!createProjectModel.adequacyTemplateCollectionId &&
    !!createProjectModel.adequacyTemplateCorrelationId &&
    createProjectModel.adequacyTemplateCorrelationId !== "" &&
    (projectAccessCodeMapSets &&
      (projectAccessCodeMapSets.length > 0 &&
        createProjectModel.accessCodeMapSetId !== null) ||
      projectAccessCodeMapSets.length === 0));
  const completionTracker: boolean[] = [];

  /*
   * Step 1: Name and Description
   */
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      name: e.target.value,
    }));
  }

  const handleDescChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      description: e.target.value,
    }));
  }

  const handleTaggingChange = (tags: ResourceTagModel[]) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      tags,
    }));
  }

  const handleIsPrivateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      isPrivate: e.target.checked,
    }));
  }

  /*
   *  Step 2: Select Template
   */
  const handleRenderTemplateCollectionsList = async () => {
    const newProjectCollections = await templateService.getUnarchivedTemplateCollections();
    const newAccuracyTemplateCollections = await accuracyTemplateService.getTemplateCollections();
    const newAccuracySpecialtyDefinitions = await accuracyTemplateService.getSpecialtyDefinitions();

    setAccuracySpecialtyDefinitions(newAccuracySpecialtyDefinitions);
    setAccuracyTemplateCollections(newAccuracyTemplateCollections);
    setProjectCollections(newProjectCollections);
  }

  const handleRenderTemplatesList = async () => {
    // const projectTemplates = (this.state.createProjectModel.categoryId !== null) ? await this.adequacyTemplateService.getTemplateCollectionById(this.state.createProjectModel.categoryId) : 0;
    // if (projectTemplates) {
    //   this.setState({ originalTemplates: projectTemplates, projectTemplates });
    // }
  }

  const handleRenderMemberPopList = async () => {
    const newAllMemberPopulations = await memberSetService.getActive(true, false);

    setAllMemberPopulations(newAllMemberPopulations);
    setAlternateMemberships(newAllMemberPopulations);
  }

  const handleExpandSpecialtyMileageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const expandSpecialtyMileageCodeMapSetId = +(e.target.value);

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      expandSpecialtyMileageCodeMapSetId,
    }));
  }

  /**
   * Loads up the available access region class map sets
   */
  const handleGatherCodeMapSets = async () => {
    const countyClassCodeMapSetsPromise = codeMapSetService.get(CodeMapType.CountyClassMap);
    const zipCodeCodeMapSetsPromise = codeMapSetService.get(CodeMapType.ZipCodeClassMap);
    const specialtyCodeMapSetsPromise = codeMapSetService.get(CodeMapType.SpecialtyCodeMap);
    const expandSpecialtyMileageCodeMapSetsPromise = codeMapSetService.get(CodeMapType.SpecialtyMileageExpansionCrosswalk);
    const specialtyToQdpCodeMapSetsPromise = codeMapSetService.get(CodeMapType.SpecialtyToQDPCodeMap);

    setAllAccessCodeMapSets([...await countyClassCodeMapSetsPromise, ...await zipCodeCodeMapSetsPromise]);
    setExpandSpecialtyMileageCodeMapSets(await expandSpecialtyMileageCodeMapSetsPromise);
    setSpecialtyCodeMapSets(await specialtyCodeMapSetsPromise);
    setSpecialtyToQdpCodeMapSets(await specialtyToQdpCodeMapSetsPromise);
  }

  const handleTemplateCollectionsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTemplateCollection = +(e.target.value);
    const newTemplateCollectionDesc = (e.target.options[e.target.selectedIndex].innerText);
    const newTemplate = projectCollections.find((t) => t.templateCollectionId === newTemplateCollection);

    const isZipProject = newTemplate.resource.resourceType === ResourceType.AdequacyZipTemplateCollection;
    // const thingsToResetIfZipProject: Partial<CreateProjectWizardState> = createThingsToResetIfZipProject(isZipProject);
    const projectThingsToResetIfZipProject: Partial<CreateProject> = createProjectThingsToResetOnAnalysisTypeChange(isZipProject, enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_VOLUME_TARGETING), enabledFeatureFlags.has(Features.ZIP_GAP_AGE_TRACKING));

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      accessCodeMapSetDescription: "",
      accessCodeMapSetId: null,
      adequacyTemplateCollectionDescription: newTemplateCollectionDesc,
      adequacyTemplateCollectionId: newTemplateCollection,
      adequacyTemplateCorrelationId: "",
      memberPopDescription: "",
      memberPopDescription2: "",
      memberSetAlias: "",
      memberSetAlias2: "",
      memberSetId: null,
      memberSetId2: null,
      timeDistanceCalculatorType: null,
      ...projectThingsToResetIfZipProject,
    }));
    setIsZipProjectTemplate(isZipProject);
    setProjectAccessCodeMapSets([]);
    setProjectMemberPopulations([]);
    setSpecialtyGroups([]);
    setTimeDistanceCalculatorTypes([]);
    setUsesPlanningRegionTemplate(false);

    if (isZipProject) {
      // template step
      setSelectedInsurances([]);
      setUseAccuracy(false);
      // member step
      setDisableMemberBindingTable(true);
    }
  }

  const handleTemplateCorrelationIdsChange = async (e: React.ChangeEvent<HTMLSelectElement>, userRoles: string[]) => {
    const newAdequacyTemplateCorrelationId = e.target.value;
    const selectedCorrelationIdOrdinal = e.target.options[e.target.selectedIndex].index;

    if (selectedCorrelationIdOrdinal === 0) {
      setCreateProjectModel((prevCreateProjectModel) => ({
        ...prevCreateProjectModel,
        adequacyTemplateCorrelationId: newAdequacyTemplateCorrelationId,
        excludedSpecialtyGroupCodes: [],
        telehealthSpecialtyGroupCodes: [],
        timeDistanceCalculatorType: null,
      }));
      setProjectAccessCodeMapSets([]);
      setProjectMemberPopulations([]);
      setSpecialtyGroups([]);
      setTimeDistanceCalculatorTypes([]);
      setUsesPlanningRegionTemplate(false);
    } else {
      // Setup the retrieval of the specialty groups while the member sets and code map sets are fetched.
      const specialtyGroupsPromise = templateService.getLatestTemplateSpecialtyGroupsByCorrelationId(createProjectModel.adequacyTemplateCollectionId, newAdequacyTemplateCorrelationId);
      const templateMetadataPromise = templateService.getLatestTemplateByCorrelationId(createProjectModel.adequacyTemplateCollectionId, newAdequacyTemplateCorrelationId, false);

      let newProjectAccessCodeMapSets: CodeMapSet[] = allAccessCodeMapSets;
      const newProjectMemberPopulations: MemberSetModel[] = allMemberPopulations;
      let newAccessCodeMapDefaultSetId: number = accessCodeMapDefaultSetId;
      let newAccessCodeMapSetId: number = createProjectModel.accessCodeMapSetId;
      let newMemberSetId: number = createProjectModel.memberSetId;

      // Get to the template, using the chosen collection, and the correlation ID
      for (let collection of projectCollections) {
        if (collection.templateCollectionId === createProjectModel.adequacyTemplateCollectionId) {
          // Populate the collection with template information
          collection = await templateService.getTemplateCollectionById(collection.templateCollectionId);

          // Found the template collection
          for (const template of collection.templates) {
            if (template.correlationId === newAdequacyTemplateCorrelationId) {
              // Found the template - now filter down the access codeMaps available for selection to those that are compatible with the chosen template
              if (template.accessCriteriaSpecificationGranularity === AccessCriteriaGranularityEnum.ZipClass) {
                newProjectAccessCodeMapSets = newProjectAccessCodeMapSets.filter((r) => r.codeMapType === CodeMapType.ZipCodeClassMap);
              } else if (template.accessCriteriaSpecificationGranularity === AccessCriteriaGranularityEnum.CountyClass) {
                newProjectAccessCodeMapSets = newProjectAccessCodeMapSets.filter((r) => r.codeMapType === CodeMapType.CountyClassMap);
              } else {
                newProjectAccessCodeMapSets = [];
              }

              // Suggest the codemapset and memberset that are specified by the template, by moving them to the top of their respective list
              if (newProjectAccessCodeMapSets && newProjectAccessCodeMapSets.length > 0 && template.accessCodeMapSetId !== null && template.accessCodeMapSetId !== undefined) {
                newProjectAccessCodeMapSets.sort((x, y) => x.codeMapSetId === template.accessCodeMapSetId ? -1 : y.codeMapSetId === template.accessCodeMapSetId ? 1 : 0);
              }
              if (template.accessCodeMapSetId !== undefined && template.accessCodeMapSetId !== null && newProjectAccessCodeMapSets !== null && newProjectAccessCodeMapSets.length > 0 && newProjectAccessCodeMapSets[0].codeMapSetId === template.accessCodeMapSetId) {
                newAccessCodeMapDefaultSetId = newProjectAccessCodeMapSets[0].codeMapSetId;
                newAccessCodeMapSetId = newProjectAccessCodeMapSets[0].codeMapSetId;
              }
              if (newProjectMemberPopulations && newProjectMemberPopulations.length > 0 && template.memberSetId !== null && template.memberSetId !== undefined) {
                newProjectMemberPopulations.sort((x, y) => x.memberSetId === template.memberSetId ? -1 : y.memberSetId === template.memberSetId ? 1 : 0);
              }
              if (template.memberSetId !== undefined && template.memberSetId !== null && newProjectMemberPopulations !== null && newProjectMemberPopulations.length > 0 && newProjectMemberPopulations[0].memberSetId === template.memberSetId) {
                newMemberSetId = newProjectMemberPopulations[0].memberSetId;
              }
            }
          }
        }
      }

      const newSpecialtyGroups = await specialtyGroupsPromise;
      const templateMetadata = await templateMetadataPromise;
      generateSpecialtyOverrides(newSpecialtyGroups);

      const specialtyDefPromise =
        specialtyDefinitionService.getSpecialtyDefinitionById(templateMetadata.specialtyDefinitionId);

      let specialtyDefinition: SpecialtyDefinition = null;

      try {
        specialtyDefinition = await specialtyDefPromise;
      } catch (e) {
        specialtyDefinition = null;
      }

      const roleCalculatorTypeMap: { [key: string]: TimeDistanceCalculatorType } = {
        [Roles.ROUTING_DISTANCE_USER]: TimeDistanceCalculatorType.GraphhopperRouting,
        [Roles.STRAIGHT_LINE_DISTANCE_USER]: TimeDistanceCalculatorType.StraightLine,
      };

      const filteredTypes: TimeDistanceCalculatorType[] = templateMetadata.timeDistanceCalculatorTypes?.filter(t =>
        t === TimeDistanceCalculatorType.Quest ||
        t === TimeDistanceCalculatorType.GeographyAware ||
        userRoles.find(r => roleCalculatorTypeMap[r] === t)) ?? [];

      const hasCmsApplicationCredits: boolean = newSpecialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);

      const showMotiveAppropriatenessScore = templateMetadata.motiveCrosswalkCodeMapSetId !== undefined && templateMetadata.motiveCrosswalkCodeMapSetId !== null;

      setAccessCodeMapDefaultSetId(newAccessCodeMapDefaultSetId);
      setCreateProjectModel((prevCreateProjectModel) => ({
        ...prevCreateProjectModel,
        accessCodeMapSetId: newAccessCodeMapSetId,
        adequacyTemplateCorrelationId: newAdequacyTemplateCorrelationId,
        excludedSpecialtyGroupCodes: [],
        memberSetId: newMemberSetId,
        showCMSCredit: !hasCmsApplicationCredits ? false : prevCreateProjectModel.showCMSCredit,
        showMotiveAppropriatenessScore: !showMotiveAppropriatenessScore ? false : prevCreateProjectModel.showMotiveAppropriatenessScore,
        telehealthSpecialtyGroupCodes: [],
        timeDistanceCalculatorType: (filteredTypes && filteredTypes.length > 0)
          ? prevCreateProjectModel.timeDistanceCalculatorType
            ? filteredTypes.find(t => t === prevCreateProjectModel.timeDistanceCalculatorType)
              ? filteredTypes.find(t => t === prevCreateProjectModel.timeDistanceCalculatorType)
              : filteredTypes[0]
            : filteredTypes[0]
          : null,
      }));
      setHideCMSApplicationCreditOption(!hasCmsApplicationCredits);
      setHideMotiveAppropriatenessScore(!showMotiveAppropriatenessScore);
      setProjectAccessCodeMapSets(newProjectAccessCodeMapSets);
      setProjectMemberPopulations(newProjectMemberPopulations);
      setSpecialtyDefinitionsMissing(!specialtyDefinition);
      setSpecialtyGroups(newSpecialtyGroups);
      setTimeDistanceCalculatorTypes(templateMetadata.timeDistanceCalculatorTypes);
      setUsesPlanningRegionTemplate(templateMetadata.usesPlanningRegion);
    }
  }

  const handleTimeDistanceCalculationTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTimeDistanceCalculatorType = e.target.value as TimeDistanceCalculatorType;

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      timeDistanceCalculatorType: newTimeDistanceCalculatorType,
    }));
  }

  const handleAccuracyTemplateCollectionsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAccuracyTemplateCollectionId = e.target.value;
    const newAccuracyTemplateCollectionDescription = e.target.options[e.target.selectedIndex].innerText;

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      accuracyTemplateCollectionDescription: newAccuracyTemplateCollectionDescription,
      accuracyTemplateCollectionId: newAccuracyTemplateCollectionId,
      accuracyTemplateCorrelationId: "",
    }));
  }

  const handleAccuracyTemplateCorrelationIdsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAccuracyTemplateCorrelationId = e.target.value;

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      accuracyTemplateCorrelationId: newAccuracyTemplateCorrelationId,
    }));
  }

  const handleAccuracySpecialtyDefChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAccuracySpecialtyDefinitionId = e.target.value;
    const newAccuracySpecialtyDefinitionDesc = e.target.options[e.target.selectedIndex].innerText;

    //reset specialty group codes accordion, then reload w/ new values
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      accuracySpecialtyDefinitionDesc: newAccuracySpecialtyDefinitionDesc,
      accuracySpecialtyDefinitionId: newAccuracySpecialtyDefinitionId,
      excludedSpecialtyGroupCodes: [],
      telehealthSpecialtyGroupCodes: [],
    }));
    setSpecialtyGroups([]);

    const specialtyDefinition = await accuracyTemplateService
      .getSpecialtyDefinitionById(newAccuracySpecialtyDefinitionId);

    const newSpecialtyGroups: TemplateSpecialtyGroup[] = [];

    for (const category of specialtyDefinition.categories) {
      for (const specialtyGroup of category.specialtyGroups) {
        const templateSpecialtyGroup: TemplateSpecialtyGroup = {
          code: specialtyGroup.code,
          displayOrder: specialtyGroup.displayOrder,
          hasTelehealthAccessPercentageCredits: null,
          name: specialtyGroup.name,
          servicingCountBy: null,
          servicingMinimumType: null,
        };

        newSpecialtyGroups.push(templateSpecialtyGroup);
      }
    }

    setSpecialtyGroups(newSpecialtyGroups);
  }

  const handleAuthorizedInsurancesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    //set state for currently selected insurance
    const uid = e.target.value;

    setSelectedInsuranceUid(uid);
  }

  const handleAddInspectedInsurance = () => {
    const uid = selectedInsuranceUid;
    const newSelectedInsurances = structuredClone(selectedInsurances);

    if (!uid) { return; }

    //get the insurance we want to add
    const insToAdd = authorizedInsurances.find((ins) => {
      return ins.uid === uid;
    });

    //check if insurance we want to add is already in list of selected insurances
    const existingIns = newSelectedInsurances.find((ins) => {
      return ins.uid === uid;
    });

    if (insToAdd && !existingIns) {
      newSelectedInsurances.push(insToAdd);

      const sortedSelectedInsurances = newSelectedInsurances && sortBy(newSelectedInsurances, "planName");

      setCreateProjectModel((prevCreateProjectModel) => ({
        ...prevCreateProjectModel,
        inspectedInsurancesList: sortedSelectedInsurances,
      }));
      setSelectedInsurances(sortedSelectedInsurances);
    }
  }

  const handleRemoveInspectedInsurance = (uid: string) => {
    const newSelectedInsurances = structuredClone(selectedInsurances);
    const index = newSelectedInsurances.findIndex((ins) => ins.uid === uid);

    if (uid && index > -1) {
      newSelectedInsurances.splice(index, 1);

      setCreateProjectModel((prevCreateProjectModel) => ({
        ...prevCreateProjectModel,
        inspectedInsurancesList: newSelectedInsurances,
      }));
      setSelectedInsurances(newSelectedInsurances);
    }
  }

  const handleUseAccuracyChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    const accuracyAndAdequacy = checked && useAdequacy;

    let sortedAuthorizedInsurances: ClientInsuranceModel[] = [...authorizedInsurances];
    let erred = false;

    if (checked && sortedAuthorizedInsurances.length === 0) {
      try {
        setAccuracyIsLoading(true);
        const authorizedInsurances = await insuranceService.getAuthorizedInsurancesByClientId();
        sortedAuthorizedInsurances = authorizedInsurances && sortBy(authorizedInsurances, "planName");
      }
      catch {
        erred = true;
      }
    }

    setAccuracyIsLoading(false);
    setAuthorizedInsurances(sortedAuthorizedInsurances);
    setAuthorizedInsurancesErred(erred);
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      accuracyAndAdequacy: accuracyAndAdequacy,
      accuracySpecialtyDefinitionDesc: null,
      accuracySpecialtyDefinitionId: null,
      accuracyTemplateCollectionDescription: null,
      accuracyTemplateCollectionId: null,
      accuracyTemplateCorrelationId: null,
      inspectedInsurancesList: [],
      showNetworkIntegrityAttestationBasedAlerts: checked ? false : prevCreateProjectModel.showNetworkIntegrityAttestationBasedAlerts,
      showNetworkIntegrityCriticalAlerts: checked ? false : prevCreateProjectModel.showNetworkIntegrityCriticalAlerts,
      showNetworkIntegrityDashboard: checked ? false : prevCreateProjectModel.showNetworkIntegrityDashboard
    }));
    setSelectedInsurances([]);
    setUseAccuracy(checked);
  }

  const handleUseAdequacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    const accuracyAndAdequacy = checked && useAccuracy;

    //reset adequacy fields when check box changes
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      accessCodeMapSetDescription: "",
      accessCodeMapSetId: null,
      accuracyAndAdequacy: accuracyAndAdequacy,
      accuracySpecialtyDefinitionDesc: null,
      accuracySpecialtyDefinitionId: null,
      adequacyTemplateCollectionDescription: "",
      adequacyTemplateCollectionId: null,
      adequacyTemplateCorrelationId: null,
      affiliationsDisplayOnNetworkAccordion: !checked ? null : prevCreateProjectModel.affiliationsDisplayOnNetworkAccordion,
      excludedSpecialtyGroupCodes: [],
      inspectedInsurancesList: [],
      marketProviderSetId: null,
      marketSpecialtyCodeMapSetId: null,
      memberPopDescription: "",
      memberPopDescription2: "",
      memberSetAlias: null,
      memberSetAlias2: null,
      memberSetId: null,
      memberSetId2: null,
      networkProviderSetId: null,
      networkSpecialtyCodeMapSetId: null,
      prospectProviderSetId: null,
      showAffiliationsInsights: !checked ? null : prevCreateProjectModel.showAffiliationsInsights,
      showCareJourneyScores: !checked ? null : prevCreateProjectModel.showCareJourneyScores,
      showLocationConfidenceScores: !checked ? null : prevCreateProjectModel.showLocationConfidenceScores,
      showMotiveAppropriatenessScore: !checked ? null : prevCreateProjectModel.showMotiveAppropriatenessScore,
      showNetworkIntegrityAttestationBasedAlerts: !checked ? null : prevCreateProjectModel.showNetworkIntegrityAttestationBasedAlerts,
      showNetworkIntegrityCriticalAlerts: !checked ? null : prevCreateProjectModel.showNetworkIntegrityCriticalAlerts,
      showNetworkIntegrityDashboard: !checked ? null : prevCreateProjectModel.showNetworkIntegrityDashboard,
      showPatternOfCareProviders: !checked ? null : prevCreateProjectModel.showPatternOfCareProviders,
      showPciDashboard: !checked ? null : prevCreateProjectModel.showPciDashboard,
      showPciData: !checked ? null : prevCreateProjectModel.showPciData,
      showPgrConfidenceTier: !checked ? null : prevCreateProjectModel.showPgrConfidenceTier,
      showTricareClaimsVolumeColumn: !checked ? null : prevCreateProjectModel.showTricareClaimsVolumeColumn,
      showVaClaimsVolumeColumn: !checked ? null : prevCreateProjectModel.showVaClaimsVolumeColumn,
      telehealthSpecialtyGroupCodes: [],
      useMarketProvidersForImpact: null,
    }));
    setProjectAccessCodeMapSets([]);
    setProjectMemberPopulations([]);
    setSpecialtyGroups([]);
    setUseAdequacy(checked);
  }

  const handleNetworkSpecialtyCodeMapSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newNetworkSpecialtyCodeMapSetId = +(e.target.value);

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      networkSpecialtyCodeMapSetId: newNetworkSpecialtyCodeMapSetId,
    }));
  }

  const handleMarketSpecialtyCodeMapSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMarketSpecialtyCodeMapSetId = +(e.target.value);

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      marketSpecialtyCodeMapSetId: newMarketSpecialtyCodeMapSetId,
    }));
  }

  const handleSpecialtyGroupsChange = (selectedRecords: TemplateSpecialtyGroup[]) => {
    const specialtyGroupCodes = specialtyGroups.map((sg) => sg.code);
    const selectedSpecialtyGroupCodes = selectedRecords.map((sg) => sg.code);
    const newExcludedSpecialtyGroupCodes = difference(specialtyGroupCodes, selectedSpecialtyGroupCodes);

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      excludedSpecialtyGroupCodes: newExcludedSpecialtyGroupCodes,
    }));
  }

  const handleTelehealthSpecialtyGroupChange = (selectedSpecialtyGroupCodes: string[]) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      telehealthSpecialtyGroupCodes: selectedSpecialtyGroupCodes,
    }));
  }

  const handleMemberPopAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    const newMemberSetAlias = e.target.value;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
        x.alias = e.target.value;
      }
    });

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
      memberSetAlias: newMemberSetAlias,
    }));
  }

  const handleMemberPopAliasChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    const newMemberSetAlias2 = e.target.value;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
        x.alias = e.target.value;
      }
    });

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
      memberSetAlias2: newMemberSetAlias2,
    }));
  }

  const toggleShowClosestProviderReportOption = (
    newCreateProjectOverrideMemberSets: CreateProjectOverrideMemberSet[],
    newMemberSetId: number = createProjectModel.memberSetId,
    newMemberSetId2: number = createProjectModel.memberSetId2,
  ) => {
    const currentMemberPops = allMemberPopulations.filter(function (pop) {
      return (pop.memberSetId == newMemberSetId || pop.memberSetId == newMemberSetId2 ||
        newCreateProjectOverrideMemberSets.some(function (overrideMemberSet) { return pop.memberSetId == overrideMemberSet.memberSetId }))
    })

    const anyCensus = currentMemberPops.some(function (pop) {
      return pop.memberSetType == MemberSetTypeEnum.Census;
    })

    let newHideClosestProviderReportOption = false;
    if (anyCensus) {
      newHideClosestProviderReportOption = true;
    }

    setHideClosestProviderReportOption(newHideClosestProviderReportOption);
  }

  const handleMemberPopChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberPop = +(e.target.value);
    const memberPopDesc = (e.target.options[e.target.selectedIndex].innerText);

    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
        x.memberSetId = memberPop;
      }
    });

    const isNotDefault = e.target.selectedIndex > 1;

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
      memberPopDescription: memberPop ? memberPopDesc : "",
      memberSetAlias: isNotDefault ? prevCreateProjectModel.memberSetAlias : "",
      memberSetId: memberPop ? memberPop : null,
      showClosestProviderReport: false,
    }));

    toggleShowClosestProviderReportOption(updates, memberPop || null, undefined);
  }

  const handleMemberPopChange2 = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberPop = +(e.target.value);
    const memberPopDesc = (e.target.options[e.target.selectedIndex].innerText);

    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
        x.memberSetId = memberPop;
      }
    });

    const isNotDefault = e.target.selectedIndex > 1;

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
      memberPopDescription2: memberPop ? memberPopDesc : "",
      memberSetAlias2: (isNotDefault) ? prevCreateProjectModel.memberSetAlias2 : "",
      memberSetId2: memberPop ? memberPop : null,
      showClosestProviderReport: false,
    }));

    toggleShowClosestProviderReportOption(updates, undefined, memberPop || null);
  }

  const generateAltMemberships = (newCreateProjectOverrideMemberSets: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets) => {
    const updates: CreateProjectOverrideMemberSet[] = [...newCreateProjectOverrideMemberSets];
    let altMembers: MemberSetModel[] = [];
    let empty = true;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
        empty = false;
        projectMemberPopulations.forEach((y) => {
          if (x.memberSetId === y.memberSetId) {
            altMembers.push(y);
          }
        });
      }
    });
    if (empty === true) {
      altMembers = projectMemberPopulations;
    }
    setAlternateMemberships(altMembers);
  }

  const handleNewAlternativeMemberPop = () => {
    const newAltMember: CreateProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: "",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Alternate,
      secondarySpecialtyGroupCodes: [],
    };

    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    updates.push(newAltMember);
    toggleMemberBinding(updates);

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
    }));
  }

  const toggleMemberBinding = (newCreateProjectOverrideMemberSets: CreateProjectOverrideMemberSet[]) => {
    let tracker = false;
    const overrides: CreateProjectOverrideMemberSet[] = newCreateProjectOverrideMemberSets;
    overrides.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate && x.memberSetId != null) {
        setDisableMemberBindingTable(false);
        tracker = true;
      }
    });
    if (tracker === false) {
      setDisableMemberBindingTable(true);
    }
  }

  const handleAlternativeMemberPopChange = (memberPopId: number, index: number) => {
    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    const memberName = allMemberPopulations.filter((ind) => ind.memberSetId === memberPopId)[0]?.resource.name;
    const x = updates[index];
    x.memberSetId = memberPopId;
    x.memberSetName = memberName;
    x.primarySpecialtyGroupCodes.forEach((y) => {
      setPrimarySpecialtyOverride(memberPopId, y);
    });
    x.secondarySpecialtyGroupCodes.forEach((y) => {
      setSecondarySpecialtyOverride(memberPopId, y);
    });

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
      showClosestProviderReport: false,
    }));

    toggleShowClosestProviderReportOption(updates, undefined, undefined);
    toggleMemberBinding(updates);
  }

  const handleAlternativeMemberPopAliasChange = (alias: string, index: number) => {
    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    updates[index].alias = alias;

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
    }));
  }

  const deleteAltMembershipRow = (index: number) => {
    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    //for (let i = index; i < updates.length - 1; i += 1) {
    //  updates[i] = { ...updates[i + 1] };
    //}
    updates.splice(index, 1);
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
      showClosestProviderReport: false,
    }));

    generateAltMemberships(updates);
    toggleMemberBinding(updates);
    toggleShowClosestProviderReportOption(updates, undefined, undefined);
  }

  const removeNonAlternateMemberships = () => {
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary || x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary || x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
        updates.splice(updates.indexOf(x), 1);
      }
    });
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
    }));
  }

  const handlePrimaryMembershipBindingChange = (memberPop: number, code: string) => {
    const updates = applyPrimaryMembershipBindingChange(createProjectModel.createProjectOverrideMemberSets, memberPop, code);
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
    }));
  }

  const handleSecondaryMembershipBindingChange = (memberPop: number, code: string) => {
    const updates = applySecondaryMembershipBindingChange(createProjectModel.createProjectOverrideMemberSets, memberPop, code);
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      createProjectOverrideMemberSets: updates,
    }));
  }

  const handleRiskPopChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const riskPop = +(e.target.value);
    const riskDescription = (e.target.options[e.target.selectedIndex].innerText);

    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
        x.memberSetId = riskPop;
      }
    });

    if (riskPop) {
      setCreateProjectModel((prevCreateProjectModel) => ({
        ...prevCreateProjectModel,
        createProjectOverrideMemberSets: updates,
        membershipRiskMemberSetAlias: riskDescription,
        membershipRiskMemberSetId: riskPop,
      }));
    } else {
      setCreateProjectModel((prevCreateProjectModel) => ({
        ...prevCreateProjectModel,
        membershipRiskMemberSetAlias: "",
        membershipRiskMemberSetId: null,
      }));
    }

  }

  const generateSpecialtyOverrides = (sortedRecords: TemplateSpecialtyGroup[]) => {
    const newSpecialtyOverrides: SpecialtyOverrides[] = [];
    sortedRecords.forEach((x) => {
      newSpecialtyOverrides.push({ code: x.code, primaryMembership: null, secondaryMembership: null });
    });
    setSpecialtyOverrides(newSpecialtyOverrides);
  }

  const setPrimarySpecialtyOverride = (memberPop: number, code: string) => {
    const newSpecialtyOverrides = structuredClone(specialtyOverrides);
    newSpecialtyOverrides.forEach((x) => {
      if (x.code === code) {
        x.primaryMembership = memberPop;
      }
    });
    setSpecialtyOverrides(newSpecialtyOverrides);
  }

  const setSecondarySpecialtyOverride = (memberPop: number, code: string) => {
    const newSpecialtyOverrides = structuredClone(specialtyOverrides);
    newSpecialtyOverrides.forEach((x) => {
      if (x.code === code) {
        x.secondaryMembership = memberPop;
      }
    });
    setSpecialtyOverrides(newSpecialtyOverrides);
  }

  const handleAccessCodeMapSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAccessCodeMapSetId = +(e.target.value);
    const newAccessCodeMapSetDescription = (e.target.options[e.target.selectedIndex].innerText);

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      accessCodeMapSetDescription: newAccessCodeMapSetId ? newAccessCodeMapSetDescription : "",
      accessCodeMapSetId: newAccessCodeMapSetId ? newAccessCodeMapSetId : null,
    }));
  }

  const handleSpecialtyToQdpCodeMapSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCodeMapSetId = +(e.target.value);
    const newCodeMapSetDescription = (e.target.options[e.target.selectedIndex].innerText);

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      accuracySpecialtyToQdpCodeMapSetDescription: newCodeMapSetDescription ? newCodeMapSetDescription : "",
      accuracySpecialtyToQdpCodeMapSetId: newCodeMapSetId ? newCodeMapSetId : null,
    }));
  }

  /*
   *  Step 4: Select Provider group or Employee group
   */
  const handleProvidersChange = (ps: ProviderSet) => {
    const networkProvider = (ps ? ps.resource.resourceId : null);
    const networkProviderDesc = (ps ? ps.resource.name : "");

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      networkProviderDescription: networkProviderDesc,
      networkProviderSetId: networkProvider,
    }));
  }

  const handleProspectsChange = (ps: ProviderSet) => {
    const prospectProvider = (ps ? ps.resource.resourceId : null);
    const prospectProviderDesc = (ps ? ps.resource.name : "");

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      prospectProviderDescription: prospectProviderDesc,
      prospectProviderSetId: prospectProvider,
    }));
  }

  const handleUseMarketChange = (state: boolean) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      useMarketProvidersForImpact: state,
      useMarketProvidersForMarketAnalysis: state,
    }));
  }

  const handleProviderSetAliasChange = (key: ProviderSetAliasKeys, value: string) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      [key]: value,
      showNextClosestNetworkProviders: (key === "nextClosestProviderSetAlias" && !!value) || prevCreateProjectModel.showNextClosestNetworkProviders,
    }));
  }

  const handleUseMarketForAnalysisChange = (state: boolean) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      useMarketProvidersForImpact: state,
      useMarketProvidersForMarketAnalysis: state,
    }));
  }

  const handleUseProspectForAnalysisChange = (state: boolean) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      useProspectProvidersForMarketAnalysis: state,
    }));
  }

  const handleUseNetworkForAnalysisChange = (state: boolean) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      useNetworkProvidersForMarketAnalysis: state,
    }));
  }

  const handleMarketChange = (ps: ProviderSet) => {
    const providerSet = ps && ps.resource ? ps : null;

    setCreateProjectModel((prevCreateProjectModel) => {
      let newUseMarketProvidersForImpact = false;
      if (!!providerSet && !!prevCreateProjectModel) {
        if (!!prevCreateProjectModel.marketProviderSetId) {
          newUseMarketProvidersForImpact = prevCreateProjectModel.useMarketProvidersForImpact;
        } else {
          newUseMarketProvidersForImpact = true;
        }
      } // else it stays false

      return {
        ...prevCreateProjectModel,
        marketProviderDescription: providerSet ? providerSet.resource.name : null,
        marketProviderSetId: providerSet ? providerSet.resource.resourceId : null,
        useMarketProvidersForImpact: newUseMarketProvidersForImpact,
      };
    })
  }

  /*
   * Step 5: Network Taxonomy
   */
  const handleNetworkTaxonomyChange = (networkTaxonomyData: NetworkTaxonomyData, isValid: boolean) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      networkTaxonomy: networkTaxonomyData,
    }));
    setIsNetworkTaxonomyValid(isValid);
  }

  /*
   *  Step 6: Select Region Set
   */
  const handleRegionSetChange = (rs: RegionSet) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      serviceAreaRegionSetId: rs.regionSetId,
    }));
    setServiceAreaRegionSet(rs);
  }

  const registerNearbyCountiesTriggerClose = () => {
    setUpdateCounties([]);
  }

  const updateLookupRegionSet = (newLookupRegionSet: LookupRegionSet) => {
    setLookupRegionSet(newLookupRegionSet);
  }

  const handleCountyStatusUpdates = async (changingStatuses: CountyStatus[]) => {
    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      countyStatuses: getUpdatedCountyStatuses(changingStatuses),
    }));
  }

  const getUpdatedCountyStatuses = (changingStatuses: CountyStatus[]) => {
    const updates: CountyStatus[] = structuredClone(createProjectModel.countyStatuses);

    for (const change of changingStatuses) {
      const indexToReplace = updates.findIndex((item) => item.countySSA === change.countySSA);
      if (indexToReplace >= 0) {
        if (change.status === RegionSetStatusType.Unknown) {
          updates.splice(indexToReplace, 1);
        } else {
          updates[indexToReplace] = change;
        }
      } else {
        if (change.status !== RegionSetStatusType.Unknown) {
          updates.push(change);
        }
      }
    }

    return updates;
  }

  const handleSubmitNearbyCounties = (newUpdateCounties: County[], regionStatus: RegionSetStatusType) => {
    const changingStatuses = newUpdateCounties.map((county) =>
    ({
      countySSA: county.countySSA,
      countyZips: [],
      status: regionStatus,
    } as CountyStatus),
    );

    setCreateProjectModel((prevCreateProjectModel) => ({
      ...prevCreateProjectModel,
      countyStatuses: getUpdatedCountyStatuses(changingStatuses),
    }));
    setUpdateCounties(newUpdateCounties);
  }

  const handleCountyStatusForCheckedState = async (counties: County[]) => {
    const countiesToUpdate: CountyStatus[] = [];
    counties.forEach((county) => {
      const currentCounty = createProjectModel.countyStatuses.find((item) => item.countySSA === county.countySSA);
      if (!currentCounty || (currentCounty && currentCounty.status === RegionSetStatusType.Unknown)) {
        countiesToUpdate.push({
          countySSA: county.countySSA,
          countyZips: [],
          regionSetId: null,
          status: RegionSetStatusType.Active,
        });
      }
    });
    handleCountyStatusUpdates(countiesToUpdate);
  }

  /*
   * Step 7: Options
   */
  const handleOptionUpdate = (update: UpdateResourceKeys) => {
    const newValue = !createProjectModel[update];
    const newProjectModel: CreateProject = {
      ...createProjectModel,
      [update]: newValue,
    };

    // if showPciData is being changed, turn off showPciDashboard, showLocationConfidenceScores, and showMotiveAppropriatenessScore, else keep current state
    if (update === "showPciData") {
      newProjectModel.showVaClaimsVolumeColumn = false;
      newProjectModel.showTricareClaimsVolumeColumn = false;
      newProjectModel.showLocationConfidenceScores = false;
      newProjectModel.showMotiveAppropriatenessScore = false;
      newProjectModel.showPciDashboard = false;
      newProjectModel.showCareJourneyScores = false;
    }

    if (update === "showNetworkIntegrityDashboard") {
      // default to turning off critical and attestation-based alerts when showing the NI dashboard
      newProjectModel.showNetworkIntegrityCriticalAlerts = false;
      newProjectModel.showNetworkIntegrityAttestationBasedAlerts = false;
    }

    // if showNCP is changing, uncheck its dependent (ncpInStateProvidersOnly)
    if (update === "showNextClosestNetworkProviders") {
      newProjectModel.ncpInStateProvidersOnly = false;
    }

    // if showNCP is unchecked, also wipe the alias
    if (update === "showNextClosestNetworkProviders" && newValue === false) {
      newProjectModel.nextClosestProviderSetAlias = "";
    }

    // if showNextClosestNetworkProviders or hasZipSummaries is being changed, turn enableRegulatoryExceptionReports and showPatternOfCareProviders off if it was true
    if ((update === "showNextClosestNetworkProviders" || update === "hasZipSummaries") && (!!newProjectModel.enableRegulatoryExceptionReports || !!newProjectModel.showPatternOfCareProviders)) {
      newProjectModel.enableRegulatoryExceptionReports = false;
      newProjectModel.showPatternOfCareProviders = false;
    }

    setCreateProjectModel(newProjectModel);
  }

  const updateExclusions = async () => {
    const showUseInMarketAnalysis = user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
    const showEnterpriseComplianceDashboard = user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);

    const newNetworkExclusions: string[] = [];
    if (createProjectModel.excludeDeactivatedFacilitiesNetwork) {
      newNetworkExclusions.push("DeactivatedFacility");
    }
    if (createProjectModel.excludeDeactivatedProvidersNetwork) {
      newNetworkExclusions.push("DeactivatedProvider");
    }
    if (createProjectModel.excludeInvalidNppesNpiFacilitiesNetwork) {
      newNetworkExclusions.push("InvalidNppesNpiFacility");
    }
    if (createProjectModel.excludeInvalidNppesNpiProvidersNetwork) {
      newNetworkExclusions.push("InvalidNppesNpiProvider");
    }
    if (createProjectModel.excludeMissingNpiFacilitiesNetwork) {
      newNetworkExclusions.push("MissingNpiFacility");
    }
    if (createProjectModel.excludeMissingNpiProvidersNetwork) {
      newNetworkExclusions.push("MissingNpiProvider");
    }
    if (createProjectModel.excludeMedicareOptOutProvidersNetwork) {
      newNetworkExclusions.push("MedicareOptOut");
    }
    if (createProjectModel.excludeOIGExcludedProvidersNetwork) {
      newNetworkExclusions.push("OIGExclusion");
    }
    if (createProjectModel.excludePrecludedProvidersNetwork) {
      newNetworkExclusions.push("Precluded");
    }

    if (newNetworkExclusions.length === 0) {
      newNetworkExclusions.push("None");
    }

    const newImpactExclusions: string[] = [];
    if (createProjectModel.excludeDeactivatedFacilitiesImpact) {
      newImpactExclusions.push("DeactivatedFacility");
    }
    if (createProjectModel.excludeDeactivatedProvidersImpact) {
      newImpactExclusions.push("DeactivatedProvider");
    }
    if (createProjectModel.excludeInvalidNppesNpiFacilitiesImpact) {
      newImpactExclusions.push("InvalidNppesNpiFacility");
    }
    if (createProjectModel.excludeInvalidNppesNpiProvidersImpact) {
      newImpactExclusions.push("InvalidNppesNpiProvider");
    }
    if (createProjectModel.excludeMissingNpiFacilitiesImpact) {
      newImpactExclusions.push("MissingNpiFacility");
    }
    if (createProjectModel.excludeMissingNpiProvidersImpact) {
      newImpactExclusions.push("MissingNpiProvider");
    }
    if (createProjectModel.excludeMedicareOptOutProvidersImpact) {
      newImpactExclusions.push("MedicareOptOut");
    }
    if (createProjectModel.excludeOIGExcludedProvidersImpact) {
      newImpactExclusions.push("OIGExclusion");
    }
    if (createProjectModel.excludePrecludedProvidersImpact) {
      newImpactExclusions.push("Precluded");
    }

    if (newImpactExclusions.length === 0) {
      newImpactExclusions.push("None");
    }

    const newProjectModel: CreateProject = {
      ...createProjectModel,
      impactExclusions: newImpactExclusions,
      networkExclusions: newNetworkExclusions,
    };
    setCreateProjectModel(newProjectModel);
    
    setHideEnterpriseComplianceDashboard(!showEnterpriseComplianceDashboard);
    setHideShowInMarketAnalysis(!showUseInMarketAnalysis);
  }

  const steps: { (props: RouteComponentProps, index: number): React.ReactNode; }[] = [];
  steps.push((props, index) =>
    <CreateProjectStepSetup
      createProjectModel={createProjectModel}
      baseUrl={routeMatch.url}
      progressList={progressList}
      onNextClick={() => { handleRenderTemplateCollectionsList(); handleRenderTemplatesList(); handleRenderMemberPopList(); handleGatherCodeMapSets(); }}
      handleNameChange={handleNameChange}
      handleDescChange={handleDescChange}
      handleTaggingChange={handleTaggingChange}
      handleIsPrivateChange={handleIsPrivateChange}
      currentIndex={index}
    />);
  completionTracker.push(createProjectModel.name !== "" && createProjectModel.description !== "")

  steps.push((props, index) =>
    <CreateProjectStepTemplate
      accuracyIsLoading={accuracyIsLoading}
      accessCodeMapDefaultSetId={accessCodeMapDefaultSetId}
      accuracyTemplateCollections={accuracyTemplateCollections}
      accuracySpecialtyDefinitions={accuracySpecialtyDefinitions}
      authorizedInsurances={authorizedInsurances}
      authorizedInsurancesErred={authorizedInsurancesErred}
      baseUrl={routeMatch.url}
      completionTracker={completionTracker}
      createProjectModel={createProjectModel}
      currentIndex={index}
      expandSpecialtyMileageCodeMapSets={expandSpecialtyMileageCodeMapSets}
      handleAccessCodeMapSetChange={handleAccessCodeMapSetChange}
      handleAccuracyTemplateCollectionsChange={handleAccuracyTemplateCollectionsChange}
      handleAccuracyTemplateCorrelationIdsChange={handleAccuracyTemplateCorrelationIdsChange}
      handleAccuracySpecialtyDefChange={handleAccuracySpecialtyDefChange}
      handleAddInspectedInsurance={handleAddInspectedInsurance}
      handleAuthorizedInsurancesChange={handleAuthorizedInsurancesChange}
      handleExpandSpecialtyMileageChange={handleExpandSpecialtyMileageChange}
      handleMarketSpecialtyCodeMapSetChange={handleMarketSpecialtyCodeMapSetChange}
      handleNetworkSpecialtyCodeMapSetChange={handleNetworkSpecialtyCodeMapSetChange}
      handleRemoveInspectedInsurance={handleRemoveInspectedInsurance}
      handleSpecialtyGroupsChange={handleSpecialtyGroupsChange}
      handleSpecialtyToQdpCodeMapSetChange={handleSpecialtyToQdpCodeMapSetChange}
      handleTelehealthSpecialtyGroupsChange={handleTelehealthSpecialtyGroupChange}
      handleTemplateCollectionsChange={handleTemplateCollectionsChange}
      handleTemplateCorrelationIdsChange={handleTemplateCorrelationIdsChange}
      handleTimeDistanceCalculationTypeChange={handleTimeDistanceCalculationTypeChange}
      handleUseAccuracyChange={handleUseAccuracyChange}
      handleUseAdequacyChange={handleUseAdequacyChange}
      isZipProject={isZipProjectTemplate}
      originalTemplateCollections={originalTemplateCollections}
      progressList={progressList}
      projectAccessCodeMapSets={projectAccessCodeMapSets}
      projectCollections={projectCollections}
      projectTemplateCollections={projectTemplateCollections}
      selectedInsuranceUid={selectedInsuranceUid}
      selectedInsurances={selectedInsurances}
      showNetworkSpecialtyCodeMap={showNetworkSpecialtyCodeMap}
      specialtyCodeMapSets={specialtyCodeMapSets}
      specialtyDefinitionsMissing={specialtyDefinitionsMissing}
      specialtyGroups={specialtyGroups}
      specialtyToQdpCodeMapSets={specialtyToQdpCodeMapSets}
      timeDistanceCalculatorTypes={timeDistanceCalculatorTypes}
      useAccuracy={useAccuracy}
      useAdequacy={useAdequacy}
    />
  );
  //we need to have values for either adequacy or accuracy
  completionTracker.push(accuracyTemplateComplete || adequacyTemplateComplete)

  steps.push((props, index) =>
    <CreateProjectStepMembers
      alternateMemberships={alternateMemberships}
      baseUrl={routeMatch.url}
      completionTracker={completionTracker}
      createProjectModel={createProjectModel}
      deleteAltMembershipRow={deleteAltMembershipRow}
      disableMemberBindingTable={disableMemberBindingTable}
      onNextClick={updateExclusions}
      generateAlternateMemberships={generateAltMemberships}
      handleMemberPopAliasChange={handleMemberPopAliasChange}
      handleMemberPopAliasChange2={handleMemberPopAliasChange2}
      handleMemberPopChange={handleMemberPopChange}
      handleMemberPopChange2={handleMemberPopChange2}
      handleRiskPopChange={handleRiskPopChange}
      handleSpecialtyGroupsChange={handleSpecialtyGroupsChange}
      isZipProject={isZipProjectTemplate}
      projectMemberPopulations={projectMemberPopulations}
      progressList={progressList}
      handlePrimaryMembershipBindingChange={handlePrimaryMembershipBindingChange}
      handleSecondaryMembershipBindingChange={handleSecondaryMembershipBindingChange}
      specialtyGroups={specialtyGroups}
      specialtyOverrides={specialtyOverrides}
      setPrimarySpecialtyOverride={setPrimarySpecialtyOverride}
      setSecondarySpecialtyOverride={setSecondarySpecialtyOverride}
      handleAlternativeMemberPopChange={handleAlternativeMemberPopChange}
      handleAlternativeMemberPopAliasChange={handleAlternativeMemberPopAliasChange}
      handleNewAlternativeMemberPop={handleNewAlternativeMemberPop}
      currentIndex={index}
    />);
  completionTracker.push(accuracyTemplateComplete || (createProjectModel.memberSetId !== null && adequacyTemplateComplete));

  steps.push((props, index) =>
    <CreateProjectStepProviders
      completionTracker={completionTracker}
      createProjectModel={createProjectModel}
      baseUrl={routeMatch.url}
      hideUseInMarketAnalysis={hideShowInMarketAnalysis}
      isZipProject={isZipProjectTemplate}
      progressList={progressList}
      onProvidersChange={handleProvidersChange}
      onProspectsChange={handleProspectsChange}
      onUseMarketChange={handleUseMarketChange}
      onMarketChange={handleMarketChange}
      onUseMarketAnalysisChange={handleUseMarketForAnalysisChange}
      onUseNetworkAnalysisChange={handleUseNetworkForAnalysisChange}
      onUseProspectAnalysisChange={handleUseProspectForAnalysisChange}
      currentIndex={index}
      networkAlias={createProjectModel.networkProviderSetAlias}
      prospectAlias={createProjectModel.prospectProviderSetAlias}
      marketAlias={createProjectModel.marketProviderSetAlias}
      ncpAlias={createProjectModel.nextClosestProviderSetAlias}
      onProviderSetAliasChange={handleProviderSetAliasChange}
    />);
  completionTracker.push(createProjectModel.networkProviderSetId !== null &&
    createProjectModel.networkProviderSetId !== 0);

  steps.push((props, index) =>
    <CreateProjectStepNetworkTaxonomy
      {...props}
      baseUrl={routeMatch.url}
      completionTracker={completionTracker}
      networkTaxonomyData={createProjectModel.networkTaxonomy}
      onNetworkTaxonomyChange={handleNetworkTaxonomyChange}
      progressList={progressList}
      currentIndex={index}
      revertTaxonomyData={networkProviderSetNetworkTaxonomy}
    />);
  completionTracker.push(isNetworkTaxonomyValid);

  steps.push((props, index) => (
    <ServiceAreaContextProvider
      countyStatuses={createProjectModel.countyStatuses}
      isZipProject={isZipProjectTemplate}
      projectName={createProjectModel.name}
      regionSet={serviceAreaRegionSet}
      usesPlanningRegion={usesPlanningRegionTemplate}
    >
      <CreateProjectStepServiceArea
        completionTracker={completionTracker}
        createProjectModel={createProjectModel}
        baseUrl={routeMatch.url}
        handleCountyStatusUpdates={handleCountyStatusUpdates}
        handleCountyStatusForCheckedState={handleCountyStatusForCheckedState}
        progressList={progressList}
        lookupRegionSet={lookupRegionSet}
        updateCounties={updateCounties}
        updateLookupRegionSet={updateLookupRegionSet}
        onRegionSetChange={handleRegionSetChange}
        onSubmitNearbyCounties={handleSubmitNearbyCounties}
        registerNearbyCountiesTriggerClose={registerNearbyCountiesTriggerClose}
        serviceAreaRegionSet={serviceAreaRegionSet}
        currentIndex={index}
      />
    </ServiceAreaContextProvider >));
  completionTracker.push(JSON.stringify(lookupRegionSet) !== "{}" && lookupRegionSet !== null);

  steps.push((props, index) =>
    <CreateProjectStepOptions
      baseUrl={routeMatch.url}
      completionTracker={completionTracker}
      createProjectModel={createProjectModel}
      handleUpdate={handleOptionUpdate}
      onNextClick={updateExclusions}
      progressList={progressList}
      
      hideClosestProviderReportOption={hideClosestProviderReportOption}
      hideCMSApplicationCredit={hideCMSApplicationCreditOption}
      hideCreateStandards={hideStandardsCreation}
      hidePracAtLocCondifence={false}
      hideProviderClaimsActivity={false}
      hideMotiveAppropriatenessScore={hideMotiveAppropriatenessScore}
      isDataShareEligible={isDataShareEligible}
      isZipProject={isZipProjectTemplate}
      currentIndex={index}
      hideEnterpriseComplianceDashboard={hideEnterpriseComplianceDashboard}
      hideStrictTimeDistance={hideStrictTimeDistance}
    />);
  completionTracker.push(true);

  steps.push((props, index) => <CreateProjectStepReview
    completionTracker={completionTracker}
    createProjectModel={createProjectModel}
    baseUrl={routeMatch.url}
    progressList={progressList}
    removeNonAlternateMemberships={removeNonAlternateMemberships}
    currentIndex={index}
    
    hideMotiveAppropriatenessScore={hideMotiveAppropriatenessScore}
    hideStrictTimeDistance={hideStrictTimeDistance}
    isDataShareEligible={isDataShareEligible}
    isZipProject={isZipProjectTemplate}
    hideEnterpriseComplianceDashboard={hideEnterpriseComplianceDashboard}
    hideCMSApplicationCredit={hideCMSApplicationCreditOption}
    hideClosestProviderReportOption={hideClosestProviderReportOption}
    hidePracAtLocCondifence={false}
    hideProviderClaimsActivity={false}
  />);

  return (
    <Switch>
      {steps.map((step, index) => (
        <Route
          key={index}
          exact={true}
          path={generatePath(`${routeMatch.url}/:id`, { id: index + 1 })}
          render={(props) => step(props, index + 1)}
        />
      ))}
    </Switch>
  );
}

export default CreateProjectWizard;
